import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowDownToLine,
  Cpu,
  FileSignature,
  FileJson,
  Pencil,
  Plus,
  Trash2,
  UploadCloud,
} from 'lucide-react';
import {
  deleteImuFirmware,
  deleteImuOtaTarget,
  downloadImuFirmwareBinary,
  downloadImuSignatureBinary,
  listImuFirmwares,
  listImuOtaTargets,
  setImuOtaTarget,
  uploadImuFirmware,
} from '../api/ImuOtaApi';
import { getImuProductionList } from '../api/ImuProductionApi';
import { ActionDrawer, ConfirmDialog, Toast } from '../components/ui';
import styles from './ImuOtaManagement.module.scss';

const views = [
  { id: 'targets', label: 'IMU rollouts', icon: Cpu },
  { id: 'firmwares', label: 'Firmware library', icon: FileJson },
];

function ImuOtaManagement() {
  const [activeView, setActiveView] = useState('targets');
  const [targets, setTargets] = useState([]);
  const [firmwares, setFirmwares] = useState([]);
  const [imuSerials, setImuSerials] = useState([]);
  const [drawer, setDrawer] = useState(null);
  const [editingTarget, setEditingTarget] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [newTargetSerial, setNewTargetSerial] = useState('');
  const [newTargetVersion, setNewTargetVersion] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const fetchTargets = useCallback(async () => {
    const response = await listImuOtaTargets();
    setTargets(response.data || []);
  }, []);

  const fetchFirmwares = useCallback(async () => {
    const response = await listImuFirmwares();
    setFirmwares(response.data || []);
  }, []);

  const fetchImuSerials = useCallback(async () => {
    try {
      const response = await getImuProductionList(1, 100);
      setImuSerials((response.data?.items || []).map(item => item.serial_number).filter(Boolean));
    } catch {
      setImuSerials([]);
    }
  }, []);

  useEffect(() => {
    Promise.all([fetchTargets(), fetchFirmwares(), fetchImuSerials()]).catch(error =>
      setErrorMessage(
        `Could not load IMU firmware workspace: ${error.response?.data?.detail || error.message}`
      )
    );
  }, [fetchFirmwares, fetchImuSerials, fetchTargets]);

  const clearMessages = () => {
    setSuccessMessage('');
    setErrorMessage('');
  };

  const closeDrawer = () => {
    if (loading || uploading) {
      return;
    }
    setDrawer(null);
    setEditingTarget(null);
    setNewTargetSerial('');
    setNewTargetVersion('');
    setUploadFile(null);
    setUploadProgress(0);
  };

  const openTargetEditor = target => {
    setEditingTarget({
      serial_number: target.serial_number,
      target_version: target.target_version || '',
    });
    setDrawer('edit-target');
  };

  const handleAddTarget = async () => {
    if (!newTargetSerial.trim()) {
      setErrorMessage('Enter an IMU serial number.');
      return;
    }
    try {
      setLoading(true);
      clearMessages();
      await setImuOtaTarget(newTargetSerial.trim(), newTargetVersion);
      setSuccessMessage(`Firmware target configured for ${newTargetSerial.trim()}.`);
      setDrawer(null);
      setNewTargetSerial('');
      setNewTargetVersion('');
      await Promise.all([fetchTargets(), fetchFirmwares()]);
    } catch (error) {
      setErrorMessage(`Could not add target: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTarget = async () => {
    if (!editingTarget) {
      return;
    }
    try {
      setLoading(true);
      clearMessages();
      await setImuOtaTarget(editingTarget.serial_number, editingTarget.target_version);
      setSuccessMessage(`Firmware target updated for ${editingTarget.serial_number}.`);
      setDrawer(null);
      setEditingTarget(null);
      await Promise.all([fetchTargets(), fetchFirmwares()]);
    } catch (error) {
      setErrorMessage(`Could not update target: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadFirmware = async () => {
    if (!uploadFile) {
      return;
    }
    try {
      setUploading(true);
      clearMessages();
      setUploadProgress(0);
      const response = await uploadImuFirmware(uploadFile, progressEvent =>
        setUploadProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total))
      );
      const { version, version_label: versionLabel } = response.data || {};
      setSuccessMessage(
        `Firmware ${versionLabel || '?'} (${version || '?'}) added to the library.`
      );
      setDrawer(null);
      setUploadFile(null);
      setUploadProgress(0);
      await fetchFirmwares();
    } catch (error) {
      setErrorMessage(
        `Could not upload firmware: ${error.response?.data?.detail || error.message}`
      );
      setUploadProgress(0);
    } finally {
      setUploading(false);
    }
  };

  const saveBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleDownload = async (version, kind) => {
    try {
      setLoading(true);
      clearMessages();
      const response =
        kind === 'signature'
          ? await downloadImuSignatureBinary(version)
          : await downloadImuFirmwareBinary(version);
      const extension = kind === 'signature' ? 'sig' : 'bin';
      saveBlob(new Blob([response.data]), `imu_firmware_${version}.${extension}`);
      setSuccessMessage(`Download for ${version} (${extension}) started.`);
    } catch (error) {
      setErrorMessage(
        `Could not download ${kind}: ${error.response?.data?.detail || error.message}`
      );
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) {
      return;
    }
    try {
      setLoading(true);
      clearMessages();
      if (pendingDelete.type === 'target') {
        await deleteImuOtaTarget(pendingDelete.serialNumber);
      } else {
        await deleteImuFirmware(pendingDelete.version);
      }
      setSuccessMessage(`${pendingDelete.label} removed.`);
      setPendingDelete(null);
      await Promise.all([fetchTargets(), fetchFirmwares()]);
    } catch (error) {
      setErrorMessage(`Could not remove item: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = bytes => {
    if (!bytes) {
      return '0 B';
    }
    const units = ['B', 'KB', 'MB', 'GB'];
    const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    return `${(bytes / 1024 ** index).toFixed(index ? 1 : 0)} ${units[index]}`;
  };
  const formatDate = isoString => (isoString ? new Date(isoString).toLocaleString() : '—');

  // DB stores the packed ZYAX form (major*10000 + minor*100 + patch);
  // show it as "0.4.4 (404)" like the hub tooling does
  const formatVersion = version => {
    if (!/^\d+$/.test(version || '')) {
      return version;
    }
    const value = Number(version);
    const major = Math.floor(value / 10000);
    const minor = Math.floor((value % 10000) / 100);
    const patch = value % 100;
    return `${major}.${minor}.${patch} (${version})`;
  };

  const knownSerials = useMemo(
    () => imuSerials.filter(serial => !targets.some(target => target.serial_number === serial)),
    [imuSerials, targets]
  );

  const viewCount = {
    targets: targets.length,
    firmwares: firmwares.length,
  };

  const versionOptions = firmwareSelectProps => (
    <select {...firmwareSelectProps}>
      <option value="">No target version</option>
      {firmwares.map(firmware => (
        <option key={firmware.version} value={firmware.version}>
          {formatVersion(firmware.version)} · {formatFileSize(firmware.firmware_size)}
        </option>
      ))}
    </select>
  );

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <div>
          <span className={styles.eyebrow}>IMU deployment</span>
          <h1>IMU firmware rollout</h1>
          <p>
            Manage target firmware per IMU serial number and store signed firmware packages
            (firmware_signed.json). IMU serials are independent from hub serial numbers.
          </p>
        </div>
      </header>

      {successMessage && (
        <Toast tone="success" onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Toast>
      )}
      {errorMessage && (
        <Toast tone="error" onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Toast>
      )}

      <nav className={styles.viewSwitcher} aria-label="IMU firmware sections">
        {views.map(view => {
          const Icon = view.icon;
          return (
            <button
              key={view.id}
              type="button"
              className={activeView === view.id ? styles.viewActive : ''}
              onClick={() => setActiveView(view.id)}
            >
              <Icon size={17} />
              <span>{view.label}</span>
              <b>{viewCount[view.id]}</b>
            </button>
          );
        })}
      </nav>

      {activeView === 'targets' && (
        <section className={styles.panel}>
          <div className={styles.panelHeader}>
            <div>
              <h2>IMU rollout targets</h2>
              <p>
                The default policy applies to every IMU without an override. IMUs check in with
                their serial and current version; a differing target triggers an update.
              </p>
            </div>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={() => setDrawer('add-target')}
            >
              <Plus size={16} /> Add IMU target
            </button>
          </div>
          {targets.length ? (
            <div className={styles.rolloutGrid}>
              {targets.map(target => {
                const isDefault = target.serial_number === 'default';
                return (
                  <article
                    className={`${styles.rolloutCard} ${isDefault ? styles.defaultCard : ''}`}
                    key={target.serial_number}
                  >
                    <div className={styles.rolloutIdentity}>
                      <span>
                        <Cpu size={17} />
                      </span>
                      <div>
                        <strong>{isDefault ? 'Default policy' : target.serial_number}</strong>
                        <small>
                          {isDefault ? 'Fallback for IMUs without an override' : 'IMU serial'}
                        </small>
                      </div>
                      <button
                        type="button"
                        className={styles.iconButton}
                        onClick={() => openTargetEditor(target)}
                        title="Edit target"
                      >
                        <Pencil size={15} />
                      </button>
                    </div>
                    <dl>
                      <div>
                        <dt>Target version</dt>
                        <dd>
                          {target.target_version
                            ? formatVersion(target.target_version)
                            : 'No target'}
                        </dd>
                      </div>
                      <div>
                        <dt>Updated</dt>
                        <dd>{formatDate(target.updated_at)}</dd>
                      </div>
                    </dl>
                    {!isDefault && (
                      <button
                        type="button"
                        className={styles.dangerLink}
                        onClick={() =>
                          setPendingDelete({
                            type: 'target',
                            serialNumber: target.serial_number,
                            label: `Target for ${target.serial_number}`,
                          })
                        }
                      >
                        <Trash2 size={14} /> Remove override
                      </button>
                    )}
                  </article>
                );
              })}
            </div>
          ) : (
            <div className={styles.emptyState}>
              <Cpu size={27} />
              <strong>No IMU rollout targets</strong>
              <p>Add a default policy or an IMU-specific firmware target.</p>
            </div>
          )}
        </section>
      )}

      {activeView === 'firmwares' && (
        <section className={styles.panel}>
          <div className={styles.panelHeader}>
            <div>
              <h2>Signed firmware library</h2>
              <p>Rollout targets can only reference firmware packages stored here.</p>
            </div>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={() => setDrawer('upload-firmware')}
            >
              <UploadCloud size={16} /> Upload firmware
            </button>
          </div>
          {firmwares.length ? (
            <div className={styles.binaryGrid}>
              {firmwares.map(firmware => {
                const references = firmware.assigned_serials || [];
                return (
                  <article className={styles.binaryCard} key={firmware.version}>
                    <div className={styles.binaryIdentity}>
                      <span>
                        <FileJson size={18} />
                      </span>
                      <div>
                        <strong>{formatVersion(firmware.version)}</strong>
                        <small>Signed firmware package</small>
                      </div>
                    </div>
                    <div className={styles.binaryMeta}>
                      <span>{formatFileSize(firmware.firmware_size)}</span>
                      <span>{formatDate(firmware.created_at)}</span>
                    </div>
                    <div className={styles.referenceInfo}>
                      {references.length
                        ? `Targeted by ${references.length} ${references.length === 1 ? 'entry' : 'entries'}: ${references.join(', ')}`
                        : 'Not currently targeted'}
                    </div>
                    <div className={styles.binaryActions}>
                      <button
                        type="button"
                        className={styles.iconButton}
                        onClick={() => handleDownload(firmware.version, 'firmware')}
                        title="Download decoded firmware (.bin)"
                      >
                        <ArrowDownToLine size={15} />
                      </button>
                      <button
                        type="button"
                        className={styles.iconButton}
                        onClick={() => handleDownload(firmware.version, 'signature')}
                        title="Download decoded signature (.sig)"
                      >
                        <FileSignature size={15} />
                      </button>
                      <button
                        type="button"
                        className={styles.iconDangerButton}
                        onClick={() =>
                          setPendingDelete({
                            type: 'firmware',
                            version: firmware.version,
                            label: `Firmware ${formatVersion(firmware.version)}`,
                          })
                        }
                        disabled={references.length > 0}
                        title={references.length ? 'Remove rollout targets first' : 'Delete'}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className={styles.emptyState}>
              <FileJson size={27} />
              <strong>No firmware packages</strong>
              <p>Upload a firmware_signed.json before creating rollout targets.</p>
            </div>
          )}
        </section>
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        title={
          pendingDelete?.type === 'target'
            ? `Remove rollout override for ${pendingDelete.serialNumber}?`
            : `Delete ${pendingDelete?.label}?`
        }
        description={
          pendingDelete?.type === 'target'
            ? 'The IMU will fall back to the default rollout policy.'
            : 'The signed firmware package will be permanently deleted. This cannot be undone.'
        }
        confirmLabel={pendingDelete?.type === 'target' ? 'Remove' : 'Delete'}
        busyLabel="Removing…"
        busy={loading}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />

      <ActionDrawer
        open={drawer === 'add-target'}
        eyebrow="IMU rollouts"
        title="Add IMU target"
        description="Assign a firmware version to one IMU serial number."
        onClose={closeDrawer}
        closeDisabled={loading}
        footer={
          <>
            <button type="button" className={styles.secondaryButton} onClick={closeDrawer}>
              Cancel
            </button>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={handleAddTarget}
              disabled={loading || !newTargetSerial.trim()}
            >
              {loading ? 'Adding…' : 'Add target'}
            </button>
          </>
        }
      >
        <div className={styles.drawerForm}>
          <label>
            <span>IMU serial number</span>
            <input
              list="imu-serial-options"
              value={newTargetSerial}
              onChange={event => setNewTargetSerial(event.target.value)}
              placeholder="Enter or select an IMU serial"
              autoFocus
            />
            <datalist id="imu-serial-options">
              {knownSerials.map(serial => (
                <option key={serial} value={serial} />
              ))}
            </datalist>
            <small>
              IMUs without an entry here follow the <b>Default policy</b> card instead.
            </small>
          </label>
          <label>
            <span>Target firmware version</span>
            {versionOptions({
              value: newTargetVersion,
              onChange: event => setNewTargetVersion(event.target.value),
            })}
          </label>
        </div>
      </ActionDrawer>

      <ActionDrawer
        open={drawer === 'edit-target'}
        eyebrow="IMU rollouts"
        title={
          editingTarget?.serial_number === 'default' ? 'Edit default policy' : 'Edit IMU target'
        }
        description={editingTarget?.serial_number || ''}
        onClose={closeDrawer}
        closeDisabled={loading}
        footer={
          <>
            <button type="button" className={styles.secondaryButton} onClick={closeDrawer}>
              Cancel
            </button>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={handleUpdateTarget}
              disabled={loading}
            >
              {loading ? 'Applying…' : 'Apply target'}
            </button>
          </>
        }
      >
        {editingTarget && (
          <div className={styles.drawerForm}>
            <label>
              <span>Target firmware version</span>
              {versionOptions({
                value: editingTarget.target_version,
                onChange: event =>
                  setEditingTarget(current => ({
                    ...current,
                    target_version: event.target.value,
                  })),
              })}
              <small>Selecting “No target version” clears the target for this entry.</small>
            </label>
          </div>
        )}
      </ActionDrawer>

      <ActionDrawer
        open={drawer === 'upload-firmware'}
        eyebrow="Firmware library"
        title="Upload signed firmware"
        description="Store a firmware_signed.json package. The version is read from the file."
        onClose={closeDrawer}
        closeDisabled={uploading}
        footer={
          <>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={closeDrawer}
              disabled={uploading}
            >
              Cancel
            </button>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={handleUploadFirmware}
              disabled={uploading || !uploadFile}
            >
              {uploading ? `Uploading ${uploadProgress}%` : 'Upload firmware'}
            </button>
          </>
        }
      >
        <div className={styles.drawerForm}>
          <label className={styles.fileDrop}>
            <UploadCloud size={28} />
            <span>{uploadFile ? uploadFile.name : 'Choose a firmware_signed.json'}</span>
            <small>
              {uploadFile
                ? formatFileSize(uploadFile.size)
                : 'JSON package with version, signature and firmware fields'}
            </small>
            <input
              type="file"
              accept=".json,application/json"
              onChange={event => setUploadFile(event.target.files?.[0] || null)}
            />
          </label>
          {uploading && (
            <progress className={styles.progress} max="100" value={uploadProgress}>
              {uploadProgress}%
            </progress>
          )}
        </div>
      </ActionDrawer>
    </div>
  );
}

export default ImuOtaManagement;
